import { useLocalization } from "@i18n";
import { translateOperationalModule } from "@i18n/operationalModules";

import type { AdminPermissionsModule } from "../../api/permissions";

interface AdminPermissionsGridProps {
  modules: AdminPermissionsModule[];
  /** Estado controlado pelo pai: `moduleId -> Set<permissionKey>`. */
  selected: Record<string, Set<string>>;
  onToggle: (moduleId: string, permissionKey: string) => void;
  /** Quando true, todos os checkboxes ficam desabilitados (ownerViewer). */
  readOnly?: boolean;
}

export const AdminPermissionsGrid = ({
  modules,
  selected,
  onToggle,
  readOnly,
}: AdminPermissionsGridProps) => {
  const { translate } = useLocalization();

  if (modules.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("admin.permissions.grid.empty")}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {modules.map((mod) => {
        const moduleSelected = selected[mod.moduleId] ?? new Set<string>();
        const isLocked = readOnly || !mod.isAvailableInStructure;
        // Nome do módulo via i18n a partir da chave canônica (sidebar.modules.*),
        // nunca a string crua do back — quality-test §0.3.3.
        const moduleLabel = translateOperationalModule(mod.moduleName, translate);

        return (
          <div
            key={mod.moduleId}
            className="flex flex-col gap-3 rounded-md border border-muted-foreground/20 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-semibold">{moduleLabel}</h3>
              {!mod.isAvailableInStructure && (
                <span className="rounded-full bg-muted-foreground/10 px-2 py-0.5 text-xs text-muted-foreground">
                  {translate("admin.permissions.grid.badge.unavailable")}
                </span>
              )}
            </div>

            {mod.catalogPermissionKeys.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {translate("admin.permissions.grid.module.noKeys")}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {mod.catalogPermissionKeys.map((key) => {
                  const checkboxId = `${mod.moduleId}:${key}`;
                  return (
                    <label
                      key={key}
                      htmlFor={checkboxId}
                      className={`flex cursor-pointer items-center gap-2 text-sm ${
                        isLocked ? "cursor-not-allowed opacity-60" : ""
                      }`}
                    >
                      <input
                        id={checkboxId}
                        type="checkbox"
                        checked={moduleSelected.has(key)}
                        disabled={isLocked}
                        onChange={() => onToggle(mod.moduleId, key)}
                        className="h-4 w-4"
                      />
                      <span>
                        {translate(`operationalPermissions.keys.${key}`)}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
